/**
 * Leaderboard run viewing (playtest request): click a row, see the run — the final
 * board drawn on the day's map and the damage-by-tower chart, exactly the reading the
 * run summary gives your own runs.
 *
 * The data is the server's `/api/leaderboard/:date/run/:playerId` payload, which it
 * derived from its OWN validation replay — a stored board can no more be faked than a
 * stored score. The map is re-derived locally from the date (same deterministic
 * generator both sides run), so nothing map-shaped needs to travel.
 */
import { useEffect, useState } from 'react';
import { CLASS_COLORS, deriveDailyRun, getTowerSafe, type MapDef } from '@prism/content';
import { SIM_VERSION, dailySeed, type BoardTowerSummary, type ClassId } from '@prism/sim';
import { MapThumb } from './MainMenu.js';

export interface PeekTarget {
  playerId: string;
  displayName: string;
  score: number;
  wavesCleared: number;
  /** Optional so the menu board can open a peek without them; the entry panel shows
   *  what it is given and simply omits the tiles it was not. */
  coreHp?: number;
  rank?: number;
  isMe?: boolean;
}

interface RunPeekProps {
  date: string;
  target: PeekTarget;
  onClose: () => void;
}

type PeekState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'missing' }
  | { status: 'ready'; board: BoardTowerSummary[]; simVersion?: number };

/** The day's seed, grouped the way the design prints it. Derived, never fetched — both
 *  sides run the same generator, so the client already knows it. */
function seedLabel(date: string): string {
  const hex = dailySeed(date).toString(16).toUpperCase().padStart(16, '0');
  return `${hex.slice(0, 4)}-${hex.slice(4, 8)}-${hex.slice(8, 12)}`;
}

/**
 * The day itself: its map AND the event it was played under.
 *
 * Deriving a daily regenerates it (~200ms), so it is cached per date for the session.
 * The mutator comes free with the map — the same generator call produces both, which is
 * why the event needs nothing from the server.
 */
interface DerivedDay {
  map: MapDef;
  mutator: { id: string; name: string; description: string } | null;
}

const dayCache = new Map<string, DerivedDay>();

function dailyDayFor(date: string): DerivedDay {
  let day = dayCache.get(date);
  if (!day) {
    const run = deriveDailyRun(date);
    day = { map: run.map, mutator: run.mutator };
    dayCache.set(date, day);
  }
  return day;
}

export function RunPeek({ date, target, onClose }: RunPeekProps) {
  const [state, setState] = useState<PeekState>({ status: 'loading' });
  const [day, setDay] = useState<DerivedDay | null>(dayCache.get(date) ?? null);

  useEffect(() => {
    let cancelled = false;
    // The map derivation is synchronous and can take ~200ms — off the click's frame.
    const timer = setTimeout(() => {
      if (!cancelled) setDay(dailyDayFor(date));
    }, 0);
    void (async () => {
      try {
        const res = await fetch(
          `/api/leaderboard/${date}/run/${encodeURIComponent(target.playerId)}`,
        );
        if (cancelled) return;
        if (res.status === 404) {
          setState({ status: 'missing' });
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { board: BoardTowerSummary[]; simVersion?: number };
        if (!cancelled) {
          setState({
            status: 'ready',
            board: body.board ?? [],
            ...(body.simVersion === undefined ? {} : { simVersion: body.simVersion }),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'error', message: err instanceof Error ? err.message : 'failed' });
        }
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [date, target.playerId]);

  const board = state.status === 'ready' ? state.board : [];
  const ranked = [...board].sort(
    (a, b) => b.damageDealt - a.damageDealt || b.assistDealt - a.assistDealt,
  );
  const maxDamage = Math.max(1, ...ranked.map((t) => Math.max(t.damageDealt, t.assistDealt)));
  const assistedTotal = board.reduce((sum, t) => sum + t.assistDealt, 0);
  /** The run was validated under a different simulation version than this build
   *  generates, so anything re-derived from the date describes a different day. */
  const stale = state.status === 'ready' && state.simVersion !== undefined
    ? state.simVersion !== SIM_VERSION
    : false;

  return (
    <div className="pause-overlay" onClick={onClose}>
      <div className="runpeek" onClick={(e) => e.stopPropagation()}>
        <span className="runpeek__accent" />

        <header className="runpeek__head">
          <span className="runpeek__who">{target.displayName || 'anonymous'}</span>
          {target.isMe && <span className="runpeek__you">YOU</span>}
          <span className="runpeek__meta">
            {date} · wave {target.wavesCleared}
            {target.rank !== undefined && ` · #${String(target.rank).padStart(2, '0')}`}
          </span>
          <span className="runpeek__score">
            {target.score.toLocaleString()} <span>pts</span>
          </span>
          <button type="button" className="runpeek__close" onClick={onClose} title="Close">
            ✕
          </button>
        </header>

        {state.status === 'loading' && <p className="muted runpeek__hint">loading run…</p>}
        {state.status === 'error' && (
          <p className="warn runpeek__hint">Could not load this run ({state.message}).</p>
        )}
        {state.status === 'missing' && (
          <p className="muted runpeek__hint">
            No saved board for this run — it was submitted before boards were stored.
          </p>
        )}

        {state.status === 'ready' && (
          <div className="runpeek__body">
            <div className="runpeek__left">
              <div className="runpeek__map">
                {day ? (
                  <MapThumb
                    map={day.map}
                    board={board}
                    scale={8}
                    className="map-thumb map-thumb--peek"
                  />
                ) : (
                  <p className="muted">drawing the map…</p>
                )}
              </div>
              {/* The design names the map ("The Furrow"), which only says something when
                  the map is an AUTHORED one. A generated daily's map is called "Daily",
                  and "on the final board · Daily" is a word that costs a reader time and
                  tells them nothing they did not get from the date in the header. */}
              <span className="runpeek__hint">
                {board.length > 0
                  ? `${board.length} tower${board.length === 1 ? '' : 's'} on the final board` +
                    (day && !day.map.id.startsWith('daily-') ? ` · ${day.map.name}` : '')
                  : 'Final board unavailable'}
              </span>

              {/*
                The day's EVENT — the mutator that was in force. It changes a run more
                than anything else on this panel does ("The Core has 15 extra HP, but
                towers deal 15% less damage"), so a board read without it is a board read
                without its rules.

                Derived, not fetched: the same `deriveDailyRun` call that draws the map
                returns the mutator, and both sides run that generator identically.

                Which is also the catch. A daily is generated per (date, SIM_VERSION), so
                deriving it under a DIFFERENT version than the run was played on
                reconstructs a different day — different map, possibly a different event.
                When the versions disagree the honest answer is to say so rather than to
                print an event this run may never have seen.
              */}
              {stale ? (
                <div className="runpeek__event runpeek__event--stale">
                  <span className="runpeek__event-name">DAY NOT RECONSTRUCTABLE</span>
                  <span className="runpeek__event-desc">
                    This run was played on simulation v{state.simVersion}; this build
                    generates v{SIM_VERSION}. The map and event for that day cannot be
                    rebuilt here, so the board above is drawn on today&rsquo;s regeneration.
                  </span>
                </div>
              ) : (
                <div className="runpeek__event">
                  <span className="runpeek__event-name">
                    {day?.mutator ? day.mutator.name.toUpperCase() : 'NO EVENT'}
                  </span>
                  <span className="runpeek__event-desc">
                    {day?.mutator ? day.mutator.description : 'A plain daily — no mutator.'}
                  </span>
                </div>
              )}

              {/* The design asked for LEAKED and GOLD SPENT. Neither is recorded: a
                  submission carries score, waves and core HP, and nothing counts leaks or
                  spend. Rather than print two numbers that would have to be invented,
                  these are the four facts a run genuinely carries. */}
              <div className="runpeek__stats">
                <div className="runpeek__stat">
                  <span className="runpeek__stat-label">CORE LEFT</span>
                  <span
                    className={`runpeek__stat-value${target.coreHp === 0 ? ' runpeek__stat-value--danger' : ''}`}
                  >
                    {target.coreHp === undefined ? '—' : target.coreHp}
                  </span>
                </div>
                <div className="runpeek__stat">
                  <span className="runpeek__stat-label">ASSISTED</span>
                  <span className="runpeek__stat-value runpeek__stat-value--gold">
                    {assistedTotal.toLocaleString()}
                  </span>
                </div>
                <div className="runpeek__stat">
                  <span className="runpeek__stat-label">SEED</span>
                  <span className="runpeek__stat-value runpeek__stat-value--small">
                    {seedLabel(date)}
                  </span>
                </div>
                <div className="runpeek__stat">
                  <span className="runpeek__stat-label">SIM</span>
                  <span className="runpeek__stat-value runpeek__stat-value--small">
                    {state.simVersion === undefined ? '—' : `v${state.simVersion}`}
                  </span>
                </div>
              </div>
            </div>

            <section className="runpeek__damage">
              <div className="runpeek__damage-head">
                <span className="runpeek__damage-title">DAMAGE BY TOWER</span>
                <span className="runpeek__damage-rule" />
                {assistedTotal > 0 && (
                  <span className="runpeek__damage-note">
                    {assistedTotal.toLocaleString()} assisted excluded from total
                  </span>
                )}
              </div>

              {ranked.length === 0 && (
                // "Nothing was placed" is only true of a run that never placed anything,
                // and a run that cleared waves is not one of those. Saying it anyway is
                // what made a missing board read as a board full of nothing.
                <span className="muted">
                  {target.wavesCleared > 0
                    ? 'No board was recorded for this run.'
                    : 'Nothing was placed.'}
                </span>
              )}

              {ranked.map((t, i) => {
                const def = getTowerSafe(t.defId);
                const color = def ? CLASS_COLORS[def.classes[0] as ClassId].color : '#c8c8d0';
                const assistOnly = t.damageDealt === 0 && t.assistDealt > 0;
                const value = assistOnly ? t.assistDealt : t.damageDealt;
                return (
                  <div
                    key={`${t.defId}#${i}`}
                    className="runpeek__bar-row"
                    title={
                      assistOnly
                        ? `${def?.name ?? t.defId} — assisted ${t.assistDealt.toLocaleString()} damage through its aura`
                        : `${def?.name ?? t.defId} — ${t.damageDealt.toLocaleString()} damage`
                    }
                  >
                    <span className="runpeek__bar-name">
                      <span className="runpeek__bar-glyph" style={{ color }}>
                        {def?.glyph ?? '?'}
                      </span>
                      <span className="runpeek__bar-label">
                        {def?.name ?? t.defId}
                        {t.star > 1 ? ` ★${t.star}` : ''}
                      </span>
                    </span>
                    <span className="runpeek__bar-track">
                      <span
                        className="runpeek__bar-fill"
                        style={{
                          width: `${Math.max(1, (value / maxDamage) * 100)}%`,
                          background: color,
                          opacity: assistOnly ? 0.45 : 1,
                        }}
                      />
                    </span>
                    <span className="runpeek__bar-value">
                      {value.toLocaleString()}
                      {assistOnly && <span className="runpeek__bar-note">assisted</span>}
                    </span>
                  </div>
                );
              })}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
